import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Panel, PanelType, Stack } from '@fluentui/react';
import {
  Button,
  Field,
  FluentProvider,
  Input,
  MessageBar,
  MessageBarBody,
  Text,
  webLightTheme,
} from '@fluentui/react-components';
import { ArrowUploadRegular } from '@fluentui/react-icons';
import { useSetAtom } from 'jotai';
import { useMutation } from '@moko/ui';
import {
  errorMessageAtom,
  successMessageAtom,
} from '../../atoms/messageBarAtoms';
import { importMetadata } from '../../services/metadataService';
import { readFileAsText } from '../../utils/csvHelpers';

interface ImportMetadataPanelProps {
  isOpen: boolean;
  onDismiss: () => void;
  onImported?: () => void;
}

interface ImportMetadataForm {
  file: File | null;
  name: string;
}

const ImportMetadataPanel = ({
  isOpen,
  onDismiss,
  onImported,
}: ImportMetadataPanelProps) => {
  const setSuccessMessage = useSetAtom(successMessageAtom);
  const setErrorMessage = useSetAtom(errorMessageAtom);
  const [importError, setImportError] = useState<string>();
  const importPackage = useMutation(
    (
      request: { pkg: unknown; name?: string },
      abortController: AbortController
    ) => importMetadata(abortController, request.pkg, request.name)
  );

  const fileInputRef = useRef<HTMLInputElement>(null);
  const packageNameRef = useRef('');
  const { control, handleSubmit, reset, setError, setValue, watch } =
    useForm<ImportMetadataForm>({
      defaultValues: { file: null, name: '' },
    });
  const file = watch('file');

  useEffect(() => {
    if (!isOpen) return;
    reset({ file: null, name: '' });
    packageNameRef.current = '';
    setImportError(undefined);
    if (fileInputRef.current) fileInputRef.current.value = '';
    importPackage.reset();
  }, [isOpen, importPackage.reset, reset]);

  const onSubmit = handleSubmit(async ({ file: selected, name }) => {
    if (!selected) return;
    setImportError(undefined);

    let text: string;
    try {
      text = await readFileAsText(selected);
    } catch (error: unknown) {
      setError('file', {
        message:
          error instanceof Error
            ? error.message
            : 'Failed to read the selected file.',
      });
      return;
    }

    let pkg: unknown;
    try {
      pkg = JSON.parse(text);
    } catch (error: unknown) {
      const detail = error instanceof Error ? ` ${error.message}` : '';
      setError('file', { message: `Invalid JSON file.${detail}` });
      return;
    }

    if (!pkg || typeof pkg !== 'object' || Array.isArray(pkg)) {
      setError('file', { message: 'The file is not a metadata package.' });
      return;
    }

    const trimmedName = name.trim();
    const outcome = await importPackage.mutate({
      pkg,
      name:
        trimmedName && trimmedName !== packageNameRef.current
          ? trimmedName
          : undefined,
    });
    if (outcome.status === 'success') {
      const response = outcome.data;
      setSuccessMessage(
        `Version ${response.version} (${response.status}). ${response.referenceValuesCreatedCount} reference values created, ${response.referenceValuesUpdatedCount} updated.`
      );
      onImported?.();
      onDismiss();
      return;
    }
    if (outcome.status === 'error') {
      setImportError(outcome.error.message);
      setErrorMessage(outcome.error.message);
    }
  });

  return (
    <Panel
      isOpen={isOpen}
      onDismiss={onDismiss}
      type={PanelType.smallFixedFar}
      closeButtonAriaLabel="Close"
      headerText="Import metadata"
    >
      <FluentProvider theme={webLightTheme}>
        <form onSubmit={onSubmit}>
          <Stack tokens={{ childrenGap: 12 }}>
            <Controller
              name="file"
              control={control}
              rules={{ required: 'Select a JSON file to import.' }}
              render={({ field, fieldState }) => (
                <Field
                  label="JSON file"
                  required
                  validationState={fieldState.error ? 'error' : 'none'}
                  validationMessage={fieldState.error?.message}
                >
                  <Stack tokens={{ childrenGap: 8 }}>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".json,application/json"
                      hidden
                      onChange={(event) => {
                        const picked = event.target.files?.[0] ?? null;
                        field.onChange(picked);
                        setValue('name', '');
                        packageNameRef.current = '';
                        if (!picked) return;
                        void readFileAsText(picked)
                          .then((text) => {
                            let parsed: { name?: unknown };
                            try {
                              parsed = JSON.parse(text) as { name?: unknown };
                            } catch (error: unknown) {
                              setError('file', {
                                message:
                                  error instanceof Error
                                    ? `Invalid JSON file. ${error.message}`
                                    : 'Invalid JSON file.',
                              });
                              return;
                            }
                            if (typeof parsed?.name !== 'string') return;
                            setValue('name', parsed.name);
                            packageNameRef.current = parsed.name;
                          })
                          .catch((error: unknown) => {
                            setError('file', {
                              message:
                                error instanceof Error
                                  ? error.message
                                  : 'Failed to read the selected file.',
                            });
                          });
                      }}
                    />
                    <Button
                      type="button"
                      icon={<ArrowUploadRegular />}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Choose File
                    </Button>
                    {field.value ? <Text>{field.value.name}</Text> : null}
                  </Stack>
                </Field>
              )}
            />
            <Text>
              Portable form codes and Order Component settings are preserved.
              Related Work form relationships are environment-local and remain
              unchanged; configure them separately in this environment.
            </Text>
            {importError && (
              <MessageBar intent="error">
                <MessageBarBody>{importError}</MessageBarBody>
              </MessageBar>
            )}
            {file ? (
              <Controller
                name="name"
                control={control}
                rules={{
                  validate: (value) =>
                    value.trim() ? true : 'Name is required.',
                }}
                render={({ field, fieldState }) => (
                  <Field
                    label="Name"
                    required
                    validationState={fieldState.error ? 'error' : 'none'}
                    validationMessage={fieldState.error?.message}
                  >
                    <Input
                      value={field.value}
                      placeholder="Name in the file"
                      onChange={(_, data) => field.onChange(data.value)}
                    />
                  </Field>
                )}
              />
            ) : null}
            <Stack horizontal tokens={{ childrenGap: 8 }}>
              <Button
                appearance="primary"
                type="submit"
                disabled={importPackage.loading}
              >
                Import
              </Button>
              <Button appearance="secondary" type="button" onClick={onDismiss}>
                Cancel
              </Button>
            </Stack>
          </Stack>
        </form>
      </FluentProvider>
    </Panel>
  );
};

export default ImportMetadataPanel;
